import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { getCategories, getProducts } from '../services/api';
import ProductCard from '../components/ProductCard';
import useSeo from '../hooks/useSeo';

export default function Collection({ onOpen }) {
  const { slug } = useParams();
  const [sort, setSort] = useState('featured');
  const [priceOpen, setPriceOpen] = useState(false);
  const [availOpen, setAvailOpen] = useState(false);
  const [priceRange, setPriceRange] = useState('all');
  const [availability, setAvailability] = useState('all');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getCategories().then(setCategories);
  }, []);

  useEffect(() => {
    getProducts({ category: slug }).then(setProducts);
  }, [slug]);

  const cat = categories.find(c => c.id === slug);

  const list = useMemo(() => {
    let l = [...products];
    if (priceRange === 'under20') l = l.filter(p => p.price < 20000);
    if (priceRange === '20to30') l = l.filter(p => p.price >= 20000 && p.price < 30000);
    if (priceRange === 'above30') l = l.filter(p => p.price >= 30000);
    if (availability === 'in')  l = l.filter(p => (p.stock_status || 'in_stock') === 'in_stock');
    if (availability === 'low') l = l.filter(p => p.stock_status === 'low_stock');
    if (sort === 'low') l = [...l].sort((a, b) => a.price - b.price);
    if (sort === 'high') l = [...l].sort((a, b) => b.price - a.price);
    return l;
  }, [products, sort, priceRange, availability]);

  const heading = slug === 'all' ? 'Shop All' : cat?.name || 'Collection';

  useSeo({
    title: heading === 'Shop All'
      ? 'Shop All Handcrafted Resin Furniture & Art'
      : `${cat?.name || 'Collection'} — Handcrafted Resin Pieces`,
    description: heading === 'Shop All'
      ? 'Browse every handcrafted resin dining table, coffee table, wall clock and wall art piece from Maa Florist Mumabi.'
      : `Handmade ${(cat?.name || 'resin').toLowerCase()} — one-of-a-kind epoxy resin pieces, made-to-order and delivered fully assembled across India.`,
    keywords: `${cat?.name || 'resin'}, handmade ${(cat?.name || 'furniture').toLowerCase()}, resin furniture India, epoxy resin`,
    type: 'website',
    image: cat?.image || (list[0] && list[0].image),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': heading,
      'url': typeof window !== 'undefined' ? window.location.href : '',
      'hasPart': list.slice(0, 12).map(p => ({
        '@type': 'Product',
        'name': p.title.split('|')[0].trim(),
        'image': p.image,
        'offers': { '@type': 'Offer', 'priceCurrency': 'INR', 'price': p.price },
      })),
    },
  });

  return (
    <main className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <nav className="flex items-center gap-1.5 text-xs text-neutral-500">
          <Link to="/" className="hover:text-[#1e3a2b]">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#1e3a2b] font-medium">{heading}</span>
        </nav>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 className="font-serif text-4xl sm:text-5xl text-neutral-900">{heading}</h1>
        {cat && <p className="mt-2 text-neutral-600 text-sm">Handcrafted {cat.name.toLowerCase()}s — no two pieces are alike.</p>}
      </section>

      {/* Filter bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 border-b border-neutral-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-neutral-700">Filter:</span>
            <div className="relative">
              <button
                onClick={() => { setPriceOpen(!priceOpen); setAvailOpen(false); }}
                className="flex items-center gap-1.5 text-sm text-neutral-800 hover:text-[#b8935a] transition-colors"
              >
                Price <ChevronDown className={`w-4 h-4 transition-transform ${priceOpen ? 'rotate-180' : ''}`} />
              </button>
              {priceOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white shadow-lg border border-neutral-200 rounded-md py-2 min-w-[200px] z-10">
                  {[
                    { v: 'all', l: 'All prices' },
                    { v: 'under20', l: 'Under Rs. 20,000' },
                    { v: '20to30', l: 'Rs. 20,000 - 30,000' },
                    { v: 'above30', l: 'Above Rs. 30,000' },
                  ].map(o => (
                    <button
                      key={o.v}
                      onClick={() => { setPriceRange(o.v); setPriceOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 ${priceRange === o.v ? 'text-[#b8935a] font-semibold' : 'text-neutral-800'}`}
                    >{o.l}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => { setAvailOpen(!availOpen); setPriceOpen(false); }}
                className="flex items-center gap-1.5 text-sm text-neutral-800 hover:text-[#b8935a] transition-colors"
              >
                Availability <ChevronDown className={`w-4 h-4 transition-transform ${availOpen ? 'rotate-180' : ''}`} />
              </button>
              {availOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white shadow-lg border border-neutral-200 rounded-md py-2 min-w-[180px] z-10">
                  {[
                    { v: 'all', l: 'All' },
                    { v: 'in', l: 'In stock' },
                    { v: 'low', l: 'Low stock' },
                  ].map(o => (
                    <button
                      key={o.v}
                      onClick={() => { setAvailability(o.v); setAvailOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 ${availability === o.v ? 'text-[#b8935a] font-semibold' : 'text-neutral-800'}`}
                    >{o.l}</button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-700">Sort by:</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="text-sm border border-neutral-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:border-[#b8935a] text-neutral-800"
              >
                <option value="featured">Featured</option>
                <option value="low">Price: Low to High</option>
                <option value="high">Price: High to Low</option>
              </select>
            </div>
            <span className="text-sm text-neutral-600">{list.length} products</span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {list.length === 0 ? (
          <div className="py-20 text-center text-neutral-500">No products found in this collection.</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
            {list.map(p => (
              <ProductCard key={p.id} product={p} onOpen={onOpen} />
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h3 className="font-serif text-2xl text-neutral-900 mb-6">Explore other collections</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.filter(c => c.id !== slug).map(c => (
            <Link key={c.id} to={`/collection/${c.id}`} className="group relative block overflow-hidden rounded-md">
              <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a2b] via-[#1e3a2b]/40 to-transparent flex items-end p-4">
                <span className="text-white font-serif text-lg">{c.name}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}